import { useState, type ReactNode } from 'react';
import {
    BUILDING_NAMES,
    SHIP_UPGRADE_MAX_LEVEL,
    type PlayerRace,
    type ScienceId,
    type ShipDefinition,
    type TickCheckpointEventOf,
} from '@kaeliux/shared';
import {
    calculateShipUpgradeCost,
    calculateShipUpgradeTime,
    canAffordCost,
    canUpgradeShip,
    getUnmetRequirements,
    getUpgradedShipStats,
    type extractBuildingLevels,
} from '@kaeliux/game-engine';
import { getShipIcon } from '../utils/shipIcons';
import { formatDuration } from '../utils/formatters';
import BuildableThumb from './BuildableThumb';
import ResourceQuantities from './ResourceQuantities';
import ShipStats from './ShipStats';
import ShipUpgradeQueueItem from './ShipUpgradeQueueItem';
import UnmetRequirementsGrid, { unmetRequirementTile } from './UnmetRequirementsGrid';
import { Button } from './ui/Button';
import { Panel } from './ui/Panel';

type ShipUpgradeEvent = TickCheckpointEventOf<'shipUpgrade'>;

export interface ShipUpgradeCardProps {
    def: ShipDefinition;
    race: PlayerRace;
    currentLevel: number;
    /** Pending upgrades of this ship, in queue order. */
    queuedEvents: ShipUpgradeEvent[];
    queueFull: boolean;
    resources: { metal: number; minerals: number; gas: number; energy: number; ozone: number };
    techCenterLevel: number;
    speedMultiplier: number;
    scienceLevels: Partial<Record<ScienceId, number>>;
    buildingLevels: ReturnType<typeof extractBuildingLevels>;
    now: number;
    /** Queues the next level. Rejects with the server's reason on failure. */
    onUpgrade: () => Promise<void>;
    /** Which queued items the viewer may cancel. */
    canCancel: (event: ShipUpgradeEvent) => boolean;
    onCancel: (event: ShipUpgradeEvent) => Promise<void>;
    /** Rush is rendered only when this is given. */
    onRush?: (event: ShipUpgradeEvent) => Promise<void>;
    darkMatter?: number;
}

/** One upgradable ship: its level and stats, the next level's price, the Upgrade button and its own queued items. */
export default function ShipUpgradeCard({
    def,
    race,
    currentLevel,
    queuedEvents,
    queueFull,
    resources,
    techCenterLevel,
    speedMultiplier,
    scienceLevels,
    buildingLevels,
    now,
    onUpgrade,
    canCancel,
    onCancel,
    onRush,
    darkMatter,
}: ShipUpgradeCardProps) {
    const queuedForThisShip = queuedEvents.length;
    const projectedLevel = currentLevel + queuedForThisShip;
    const atMax = projectedLevel >= SHIP_UPGRADE_MAX_LEVEL;
    const nextLevel = projectedLevel + 1;

    const [isRequesting, setIsRequesting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const nextCost = atMax ? null : calculateShipUpgradeCost(race, def.id, nextLevel);
    const nextTimeSeconds = atMax
        ? null
        : calculateShipUpgradeTime(race, def.id, nextLevel, techCenterLevel, speedMultiplier);

    const unmetRequirements = getUnmetRequirements(def.requirements, buildingLevels, scienceLevels);
    const canPrereq = unmetRequirements.length === 0;
    const affordable = nextCost ? canAffordCost(nextCost, resources) : false;
    const canUpgrade = canUpgradeShip(race, def.id, projectedLevel, buildingLevels, scienceLevels);

    const disabled = !canUpgrade || queueFull || !affordable || isRequesting;
    const disabledReason: ReactNode = atMax
        ? 'Max level'
        : !canPrereq
            ? null // shown as the full-width UnmetRequirementsGrid below instead
            : techCenterLevel < 1
                ? `${BUILDING_NAMES.techCenter} required`
                : queueFull
                    ? 'Queue full'
                    : !affordable
                        ? 'Insufficient resources'
                        : null;

    async function handleUpgrade() {
        setIsRequesting(true);
        setError(null);
        try {
            await onUpgrade();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Upgrade failed');
        } finally {
            setIsRequesting(false);
        }
    }

    const currentStats = getUpgradedShipStats(def, currentLevel);
    const percentLabel = def.category === 'war' ? 'Attack & Life' : def.id === 11 ? 'Speed' : 'Cargo';

    const shipImage = (
        <div className="w-full h-full rounded-control border border-hairline bg-surface-void/60 flex items-center justify-center overflow-hidden">
            <img src={getShipIcon(race, def.id)} alt={def.name} className="w-full h-full object-contain" />
        </div>
    );

    return (
        <Panel
            variant="solid"
            padding="md"
            className="flex flex-col gap-3"
            data-tutorial-id={`ship-upgrade-${def.id}`}
            data-testid="ship-upgrade-card"
        >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1 min-w-0 flex flex-col sm:flex-row items-start gap-3">
                    <BuildableThumb
                        image={shipImage}
                        countBadge={currentLevel}
                        countTitle={`Upgrade level ${currentLevel}`}
                        cost={atMax ? {} : (nextCost ?? {})}
                        costLabel={atMax ? undefined : 'Next level'}
                        progressPct={(currentLevel / SHIP_UPGRADE_MAX_LEVEL) * 100}
                        progressTitle={`Level ${currentLevel} / ${SHIP_UPGRADE_MAX_LEVEL}`}
                        imageBoxClassName="w-32 sm:w-36 aspect-[4/3]"
                    />
                    <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-title font-semibold text-content-primary">{def.name}</h3>
                            <span className="text-content-muted text-body">
                                Level {currentLevel}{queuedForThisShip > 0 ? ` (+${queuedForThisShip})` : ''} / {SHIP_UPGRADE_MAX_LEVEL}
                            </span>
                        </div>
                        <p className="text-content-muted text-body">Improves {percentLabel}</p>
                        <p className="text-content-muted text-label mt-1">
                            <ShipStats stats={currentStats} />
                        </p>
                        {!atMax && nextCost && nextTimeSeconds !== null && (
                            <p className="text-content-secondary text-body mt-2">
                                <span className="text-content-muted mr-2">Next:</span>
                                <ResourceQuantities amounts={nextCost} />
                                <span className="text-content-muted ml-2">· {formatDuration(nextTimeSeconds)}</span>
                            </p>
                        )}
                        {error && <p className="text-status-danger text-label mt-1">{error}</p>}
                    </div>
                </div>
                <div className="flex flex-col items-start sm:items-end gap-1">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleUpgrade}
                        disabled={disabled}
                    >
                        {isRequesting ? 'Queuing…' : atMax ? 'Max' : 'Upgrade'}
                    </Button>
                    {disabledReason && !isRequesting && (
                        <span className="text-content-muted text-label">{disabledReason}</span>
                    )}
                </div>
            </div>
            {unmetRequirements.length > 0 && !isRequesting && (
                <UnmetRequirementsGrid tiles={unmetRequirements.map((req) => unmetRequirementTile(req, race))} />
            )}
            {queuedEvents.length > 0 && (
                <div className="flex flex-wrap gap-3 border-t border-hairline pt-2">
                    {queuedEvents.map((event) => (
                        <ShipUpgradeQueueItem
                            key={event.eventId}
                            race={race}
                            event={event}
                            now={now}
                            canCancel={canCancel(event)}
                            onCancel={() => onCancel(event)}
                            onRush={onRush ? () => onRush(event) : undefined}
                            darkMatter={darkMatter}
                        />
                    ))}
                </div>
            )}
        </Panel>
    );
}
